import ConceptSwitcher from "@/components/ConceptSwitcher";
import BrandMark, { TAGLINE } from "@/components/BrandMark";
import { PHOTOS } from "@/constants/photos";
import {
  ArrowUpRight,
  ArrowRight,
  Quotes,
  Compass,
  ChalkboardTeacher,
  Users,
  ShieldCheck,
  Microphone,
  Sparkle,
} from "@phosphor-icons/react";

// Concept 03 — Human-Centered Authority palette
const C = {
  bg: "#F7F2EA", // Soft Cream
  surface: "#EFE4DA", // Warm Sand
  text: "#2A2523", // Deep Cocoa
  brand: "#486B68", // Muted Moss Teal
  rose: "#C28F90", // Dusty Rose
  muted: "#736A67", // Earth Gray
  line: "#DDD1C6", // Soft Linen Line
  inverse: "#2F4745", // Deep Olive Ink
  porcelain: "#FBF7F1", // Porcelain
};

const navLinks = ["Who We Help", "How We Help", "Speaking", "About Katrina", "Insights", "Contact"];

const stuckPatterns = [
  {
    title: "The leader who stopped sleeping but won’t name why.",
    body:
      "Carrying a transition, board tension, or sensitive personnel question alone — with no obvious person to bring it to.",
    tag: "Leadership pressure",
  },
  {
    title: "The supervisor doing their best, exposed every day.",
    body:
      "Promoted on competence. Asked to lead people. Never trained for the conversations the role actually requires.",
    tag: "Supervisory exposure",
  },
  {
    title: "The team that learned to work around a conduct issue.",
    body:
      "Trust quietly draining while everyone stays professional. Culture work feels too vague; legal feels too sharp.",
    tag: "Conduct & culture",
  },
  {
    title: "The organization growing faster than its people practices.",
    body:
      "New programs, new hires, same informal systems. Founders or EDs holding too much institutional memory alone.",
    tag: "Scale & systems",
  },
];

const services = [
  {
    Icon: Compass,
    label: "Advisory and strategic guidance",
    body: "A trusted thinking partner for sensitive people and leadership decisions.",
    bestWhen: "Best when leadership stakes are real and the path forward isn’t obvious.",
  },
  {
    Icon: ChalkboardTeacher,
    label: "Supervisory readiness",
    body: "Practical, HR-aware development that equips managers with judgment and language.",
    bestWhen: "Best when managers are inconsistent, exposed, or under-trained for hard moments.",
  },
  {
    Icon: Users,
    label: "Coaching for leaders",
    body: "Quiet, candid coaching for leaders carrying real organizational weight.",
    bestWhen: "Best when an executive needs an outside thinking partner, not a curriculum.",
  },
  {
    Icon: ShieldCheck,
    label: "Facilitation for sensitive moments",
    body: "Steady facilitation when conduct, culture, or trust needs careful handling.",
    bestWhen: "Best when a situation is too delicate for HR alone and too human for legal.",
  },
  {
    Icon: Microphone,
    label: "Speaking and learning spaces",
    body: "Bring the same judgment into your audience — and back into your team.",
    bestWhen: "Best for boards, leadership offsites, and mission-aligned audiences.",
  },
];

const engagementFeel = [
  {
    label: "First conversation",
    principle: "Discovery without obligation",
    body:
      "30 minutes. We listen, name what we’re hearing, and decide together whether to keep going. No deck, no pitch.",
  },
  {
    label: "Honest shaping",
    principle: "Scoping with discipline",
    body:
      "If we’re a fit, we co-design the engagement — scope, sequencing, deliverables. If we’re not, we say so and often refer.",
  },
  {
    label: "Held with care",
    principle: "Confidential delivery",
    body:
      "We work within your rhythms. Confidentially. Without performing urgency. Without making the moment louder than it needs to be.",
  },
  {
    label: "Equipped after",
    principle: "Capability transfer",
    body:
      "We leave your leaders, supervisors, and teams more capable than we found them — and ready to hold the work themselves.",
  },
];

const thoughtLeadership = [
  {
    type: "Speaking",
    label: "Keynote · 2025",
    title: "Leading people when the playbook stops working.",
    meta: "Mission-driven leadership summit",
  },
  {
    type: "Essay",
    label: "Field note",
    title: "What conduct conversations require of leaders — and what they cost.",
    meta: "7 min read",
  },
  {
    type: "Workshop",
    label: "Workshop series",
    title: "Supervisor readiness for organizations growing past 100.",
    meta: "4-part series · cohort",
  },
];

export default function ConceptC() {
  return (
    <main
      data-testid="concept-c-page"
      className="min-h-screen font-work antialiased"
      style={{ background: C.bg, color: C.text }}
    >
      <ConceptSwitcher tone="c" />

      {/* Navigation */}
      <header
        className="backdrop-blur border-b"
        style={{ background: `${C.bg}F2`, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 h-28 flex items-center justify-between">
          <div className="flex items-end gap-5">
            <BrandMark href="/concept-c" size="lg" testId="nav-wordmark" />
            <span
              className="hidden xl:inline-flex items-center font-playfair italic text-[16px] leading-tight pb-2 border-l pl-5 max-w-[240px]"
              style={{ color: C.brand, borderColor: `${C.brand}55` }}
            >
              {TAGLINE}
            </span>
          </div>
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navLinks.map((l) => (
              <a
                key={l}
                href="#"
                data-testid={`nav-link-${l.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[13px] font-medium transition-colors whitespace-nowrap"
                style={{ color: `${C.text}D0` }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.brand)}
                onMouseLeave={(e) => (e.currentTarget.style.color = `${C.text}D0`)}
              >
                {l}
              </a>
            ))}
          </nav>
          <a
            href="#inquiry"
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 px-6 py-3 text-[13px] rounded-full transition-colors whitespace-nowrap"
            style={{ background: C.brand, color: C.porcelain }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.inverse)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.brand)}
          >
            Start the conversation <ArrowUpRight size={13} weight="bold" />
          </a>
        </div>
      </header>

      {/* HERO — close warm portrait leads */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -right-32 -top-10 w-[600px] h-[600px] rounded-full blur-3xl -z-10"
          style={{ background: `${C.surface}AA` }}
        />
        <div
          aria-hidden="true"
          className="absolute -left-40 -bottom-32 w-[500px] h-[500px] rounded-full blur-3xl -z-10"
          style={{ background: `${C.rose}29` }}
        />

        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div
                className="inline-flex items-center gap-2 border rounded-full px-4 py-1.5 mb-8"
                style={{ background: `${C.surface}99`, borderColor: `${C.text}1F` }}
              >
                <Sparkle size={12} weight="fill" style={{ color: C.rose }} />
                <span
                  className="text-[11px] uppercase tracking-[0.22em] font-medium"
                  style={{ color: C.text }}
                >
                  {TAGLINE}
                </span>
              </div>

              <h1
                data-testid="hero-title"
                className="font-playfair font-normal text-4xl sm:text-5xl lg:text-[72px] leading-[1.06] tracking-tight"
                style={{ color: C.text }}
              >
                Faith 1st helps organizations navigate people and culture challenges with{" "}
                <em className="italic" style={{ color: C.brand }}>
                  clarity, care, and courage
                </em>
                .
              </h1>

              <p
                data-testid="hero-subcopy"
                className="mt-10 max-w-xl text-[18px] lg:text-[19px] leading-[1.6]"
                style={{ color: `${C.text}D5` }}
              >
                An advisory practice for executive directors, boards, and HR
                leaders navigating leadership transitions, supervisory exposure,
                culture strain, and sensitive conduct work — held with
                discretion, strategic structure, and a steady human point of
                view.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="#inquiry"
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center gap-2 px-8 py-4 text-[14.5px] rounded-full transition-colors"
                  style={{ background: C.brand, color: C.porcelain }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.inverse)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.brand)}
                >
                  Start the conversation
                  <ArrowRight
                    size={15}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
                <a
                  href="#about-katrina"
                  data-testid="hero-secondary-cta"
                  className="group inline-flex items-center gap-1.5 text-[12.5px] uppercase tracking-[0.18em] font-medium transition-colors"
                  style={{ color: `${C.text}88` }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.brand)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = `${C.text}88`)}
                >
                  Meet the practice
                  <ArrowRight
                    size={12}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>

              <div
                className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12px]"
                style={{ color: `${C.text}88` }}
              >
                {[
                  "Mission-driven nonprofits",
                  "Foundations & associations",
                  "Faith-based organizations",
                  "Mission-driven employers",
                ].map((t) => (
                  <span key={t} className="flex items-center gap-2">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: C.rose }}
                    />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — close warm portrait (IMG_0784) */}
            <div className="lg:col-span-5">
              <div className="relative">
                <figure
                  className="relative rounded-[28px] overflow-hidden aspect-[4/5]"
                  style={{
                    background: C.surface,
                    boxShadow: `0 30px 60px -20px ${C.inverse}33`,
                  }}
                >
                  <img
                    src={PHOTOS.warmCloseUp}
                    alt="Katrina Bernard — Founder & Principal of Faith 1st"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                  />
                </figure>

                {/* Floating quote card */}
                <div
                  className="absolute -left-4 sm:-left-6 -bottom-6 max-w-[280px] rounded-2xl border p-5"
                  style={{
                    background: C.porcelain,
                    borderColor: `${C.text}14`,
                    boxShadow: `0 20px 40px -12px ${C.text}2E`,
                  }}
                >
                  <Quotes size={20} weight="fill" className="mb-3" style={{ color: C.rose }} />
                  <p
                    className="font-playfair italic text-[16px] leading-[1.45]"
                    style={{ color: C.text }}
                  >
                    “Warmth that can name hard truths. Authority that doesn’t need to perform.”
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRACTICE PROFILE — firm-level structural proof bar (added to balance founder warmth) */}
      <section
        data-testid="practice-profile"
        className="border-y"
        style={{ background: C.inverse, color: C.porcelain, borderColor: C.line }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-12 lg:py-14">
          <div className="flex items-center gap-4 mb-9">
            <span
              className="inline-block h-px w-12"
              style={{ background: C.rose }}
            />
            <p
              className="text-[11px] uppercase tracking-[0.32em] font-medium"
              style={{ color: C.rose }}
            >
              Faith&nbsp;1st &nbsp;·&nbsp; The practice
            </p>
            <span
              className="hidden sm:inline-block h-px flex-1"
              style={{ background: `${C.porcelain}1F` }}
            />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px">
            {[
              {
                k: "Five disciplines",
                v: "Legal context, HR practice, executive coaching, sensitive facilitation, leadership development — held in one practice.",
              },
              {
                k: "Confidentiality",
                v: "Discretion is the operating posture, not a feature. We work behind the work — never in front of it.",
              },
              {
                k: "Scoped engagements",
                v: "Each engagement is shaped to the moment — from a single conversation to embedded multi-month support.",
              },
              {
                k: "Built for",
                v: "Mission-driven nonprofits, foundations, faith-led, and association leadership teams between 50 and 500 people.",
              },
            ].map((b, i) => (
              <div
                key={b.k}
                data-testid={`practice-profile-${i}`}
                className="p-7 lg:p-8 flex flex-col"
                style={{
                  background: C.inverse,
                  borderLeft: i > 0 ? `1px solid ${C.porcelain}1F` : "none",
                }}
              >
                <span
                  className="text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-4"
                  style={{ color: C.rose }}
                >
                  0{i + 1}
                </span>
                <h3
                  className="font-playfair text-[22px] leading-[1.15] tracking-tight mb-3"
                  style={{ color: C.porcelain }}
                >
                  {b.k}
                </h3>
                <p
                  className="text-[13.5px] leading-[1.7]"
                  style={{ color: `${C.porcelain}C8` }}
                >
                  {b.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder-led credibility — EARLY */}
      <section
        id="about-katrina"
        data-testid="katrina-credibility"
        style={{ background: C.surface }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-7">
                <span
                  className="inline-block h-px w-10"
                  style={{ background: C.brand }}
                />
                <p
                  className="text-[11px] uppercase tracking-[0.28em] font-medium"
                  style={{ color: C.brand }}
                >
                  Led by Katrina Bernard
                </p>
              </div>
              <h2
                data-testid="katrina-title"
                className="font-playfair font-normal text-3xl sm:text-4xl lg:text-[52px] leading-[1.08] tracking-tight"
                style={{ color: C.text }}
              >
                A practitioner who can{" "}
                <em className="italic" style={{ color: C.brand }}>
                  hold complexity
                </em>{" "}
                — and name truth without losing the room.
              </h2>
              <p
                className="mt-8 text-[16.5px] leading-[1.65] max-w-md"
                style={{ color: `${C.text}D0` }}
              >
                Faith&nbsp;1st reflects two decades of practice across mission-driven
                organizations — combining employment-law context, senior HR
                judgment, certified executive coaching, and sensitive
                facilitation in a single practice that leaders actually return to.
              </p>

              <a
                href="#"
                className="group mt-10 inline-flex items-center gap-2 text-[12.5px] uppercase tracking-[0.2em] font-semibold pb-1.5 border-b transition-colors"
                style={{ color: C.brand, borderColor: C.brand }}
              >
                Full bio &amp; background
                <ArrowRight
                  size={13}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>

            <div className="lg:col-span-7">
              <dl className="grid sm:grid-cols-2 gap-4">
                {[
                  {
                    t: "Employment-law fluent",
                    b: "Frames every decision with the legal context boards and HR teams actually rely on.",
                  },
                  {
                    t: "Senior HR practice",
                    b: "Operating-level HR judgment built across mission-driven organizations — not theory.",
                  },
                  {
                    t: "Certified executive coaching",
                    b: "Trained to support leaders carrying real organizational stakes, not perform leadership.",
                  },
                  {
                    t: "Sensitive facilitation",
                    b: "Equipped for the conduct, culture, and trust rooms most consultants quietly avoid.",
                  },
                  {
                    t: "Nonprofit & faith-led fluency",
                    b: "Built for purpose-driven boards and leadership teams — not consumer brands.",
                  },
                  {
                    t: "Confidential by default",
                    b: "Discretion is the operating posture. No public client lists. No optics-led delivery.",
                  },
                ].map(({ t, b }) => (
                  <div
                    key={t}
                    className="rounded-2xl p-6 border transition-colors"
                    style={{
                      background: C.porcelain,
                      borderColor: `${C.text}1F`,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.borderColor = `${C.brand}66`)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.borderColor = `${C.text}1F`)
                    }
                  >
                    <dt
                      className="font-playfair text-[20px] leading-tight tracking-tight mb-2"
                      style={{ color: C.text }}
                    >
                      {t}
                    </dt>
                    <dd
                      className="text-[13.5px] leading-[1.65]"
                      style={{ color: `${C.text}C0` }}
                    >
                      {b}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Problem section — Where organizations get stuck */}
      <section data-testid="stuck-section" style={{ background: C.bg }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-7">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                Some of the hardest leadership work is deeply human
              </p>
              <h2
                className="font-playfair font-normal text-3xl sm:text-4xl lg:text-[52px] leading-[1.06] tracking-tight"
                style={{ color: C.text }}
              >
                The patterns capable leaders{" "}
                <em className="italic" style={{ color: C.brand }}>
                  recognize
                </em>{" "}
                — and quietly carry alone.
              </h2>
            </div>
            <p
              className="lg:col-span-5 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.text}D0` }}
            >
              Culture strain, leadership transitions, supervision gaps, trust
              concerns, and difficult people dynamics affect more than
              operations. They affect the health of the whole organization.
              Faith&nbsp;1st helps leaders address those realities with wisdom,
              steadiness, and strategic support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
            {stuckPatterns.map((p, i) => (
              <article
                key={i}
                data-testid={`stuck-pattern-${i}`}
                className="rounded-2xl p-7 lg:p-9 border transition-colors"
                style={{ background: `${C.surface}99`, borderColor: `${C.text}1F` }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${C.rose}66`)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${C.text}1F`)}
              >
                <div className="flex items-start justify-between mb-5">
                  <span
                    className="text-[10.5px] uppercase tracking-[0.24em] font-semibold inline-flex items-center gap-2"
                    style={{ color: C.brand }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: C.brand }}
                    />
                    {p.tag}
                  </span>
                  <span
                    className="font-playfair italic text-[22px]"
                    style={{ color: C.rose }}
                  >
                    0{i + 1}
                  </span>
                </div>
                <h3
                  className="font-playfair font-normal text-[23px] lg:text-[24px] leading-[1.22] mb-4"
                  style={{ color: C.text }}
                >
                  {p.title}
                </h3>
                <p
                  className="text-[15px] leading-[1.65]"
                  style={{ color: `${C.text}C8` }}
                >
                  {p.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Service pathways */}
      <section
        id="services"
        data-testid="services-section"
        style={{ background: `${C.surface}80` }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-6">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                Service pathways
              </p>
              <h2
                className="font-playfair font-normal text-3xl sm:text-4xl lg:text-[48px] leading-[1.06] tracking-tight"
                style={{ color: C.text }}
              >
                Five paths.{" "}
                <em className="italic" style={{ color: C.brand }}>
                  One
                </em>{" "}
                steady partner.
              </h2>
            </div>
            <p
              className="lg:col-span-5 lg:col-start-8 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.text}D0` }}
            >
              Most engagements begin with a single conversation. From there, we
              shape the work to the moment — and stay close as it evolves.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => {
              const Icon = s.Icon;
              return (
                <article
                  key={s.label}
                  data-testid={`service-${i}`}
                  className="group rounded-2xl p-7 lg:p-8 border transition-all duration-300 flex flex-col"
                  style={{ background: C.porcelain, borderColor: `${C.text}1F` }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${C.brand}66`;
                    e.currentTarget.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = `${C.text}1F`;
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div className="flex items-start justify-between mb-6">
                    <span
                      className="inline-flex w-12 h-12 items-center justify-center rounded-full border"
                      style={{ background: C.surface, borderColor: `${C.text}14` }}
                    >
                      <Icon size={22} weight="light" style={{ color: C.brand }} />
                    </span>
                    <span
                      className="text-[10.5px] uppercase tracking-[0.22em] font-semibold"
                      style={{ color: C.rose }}
                    >
                      0{i + 1} / 05
                    </span>
                  </div>
                  <h3
                    className="font-playfair font-normal text-[23px] lg:text-[24px] leading-[1.15] tracking-tight mb-4"
                    style={{ color: C.text }}
                  >
                    {s.label}
                  </h3>
                  <p
                    className="text-[14.5px] leading-[1.65] mb-5"
                    style={{ color: `${C.text}CC` }}
                  >
                    {s.body}
                  </p>
                  <p
                    className="font-playfair italic text-[14px] leading-[1.5] mb-7 pt-4 border-t flex-1"
                    style={{ color: C.brand, borderColor: `${C.text}14` }}
                  >
                    {s.bestWhen}
                  </p>
                  <a
                    href="#inquiry"
                    className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em] font-semibold pb-1.5 border-b w-fit transition-all"
                    style={{ color: C.brand, borderColor: `${C.brand}55` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = C.brand;
                      e.currentTarget.style.gap = "12px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${C.brand}55`;
                      e.currentTarget.style.gap = "8px";
                    }}
                  >
                    Explore fit <ArrowRight size={13} weight="bold" />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process — what engagement feels like */}
      <section
        data-testid="engagement-section"
        style={{ background: C.inverse, color: C.porcelain }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-6">
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                Support that meets the moment
              </p>
              <h2
                className="font-playfair font-normal text-3xl sm:text-4xl lg:text-[48px] leading-[1.06] tracking-tight"
                style={{ color: C.porcelain }}
              >
                Support that meets the moment{" "}
                <em className="italic" style={{ color: C.rose }}>
                  without losing the mission
                </em>
                .
              </h2>
            </div>
            <p
              className="lg:col-span-5 lg:col-start-8 text-[16.5px] leading-[1.65]"
              style={{ color: `${C.porcelain}D5` }}
            >
              Faith 1st begins by understanding what the organization is
              carrying, what support is needed, and what kind of engagement will
              serve people well. The work is shaped with care, clarity, and a
              commitment to meaningful movement.
            </p>
          </div>

          <div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-px border"
            style={{ background: `${C.porcelain}26`, borderColor: `${C.porcelain}26` }}
          >
            {engagementFeel.map((e, i) => (
              <div
                key={e.label}
                data-testid={`engagement-step-${i}`}
                className="p-8 lg:p-9 flex flex-col"
                style={{ background: C.inverse }}
              >
                <div className="flex items-center gap-3 mb-7">
                  <span
                    className="font-playfair italic text-[28px] leading-none"
                    style={{ color: C.rose }}
                  >
                    0{i + 1}
                  </span>
                  <span
                    className="h-px flex-1"
                    style={{ background: `${C.rose}55` }}
                  />
                </div>
                <p
                  className="text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-3"
                  style={{ color: C.rose }}
                >
                  {e.principle}
                </p>
                <h3
                  className="font-playfair font-medium text-[26px] leading-[1.1] mb-4"
                  style={{ color: C.porcelain }}
                >
                  {e.label}
                </h3>
                <p
                  className="text-[14px] leading-[1.7]"
                  style={{ color: `${C.porcelain}C8` }}
                >
                  {e.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seated portrait — reinforces executive weight after warmth */}
      <section style={{ background: C.bg }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <figure
                className="relative aspect-[4/5] overflow-hidden rounded-[24px] border"
                style={{ borderColor: C.line, background: C.surface }}
              >
                <img
                  src={PHOTOS.seatedFormal}
                  alt="Katrina Bernard — executive seated portrait"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </figure>
            </div>
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-7">
                <span
                  className="inline-block h-px w-10"
                  style={{ background: C.brand }}
                />
                <p
                  className="text-[11px] uppercase tracking-[0.28em] font-medium"
                  style={{ color: C.brand }}
                >
                  The same authority — at the table
                </p>
              </div>
              <h2
                className="font-playfair font-normal text-3xl sm:text-4xl lg:text-[46px] leading-[1.08] tracking-tight"
                style={{ color: C.text }}
              >
                Warm in conversation.{" "}
                <em className="italic" style={{ color: C.brand }}>
                  Composed
                </em>{" "}
                when the stakes get high.
              </h2>
              <p
                className="mt-8 max-w-2xl text-[16.5px] leading-[1.65]"
                style={{ color: `${C.text}D0` }}
              >
                The discernment Katrina brings to a coffee with a stretched
                executive director is the same discernment she brings to a
                boardroom discussing transition risk, conduct concerns, or
                supervisory exposure. Different rooms. Same posture.
              </p>

              {/* Proof signal strip */}
              <div
                className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-px border rounded-lg overflow-hidden max-w-xl"
                style={{ background: C.line, borderColor: C.line }}
              >
                {[
                  ["Two decades", "of practice"],
                  ["Boards · EDs", "primary audience"],
                  ["Nonprofit, faith-led, associations", "core context"],
                ].map(([k, v], i) => (
                  <div
                    key={i}
                    className="p-4 flex flex-col"
                    style={{ background: C.bg }}
                  >
                    <span
                      className="font-playfair text-[18px] leading-tight tracking-tight"
                      style={{ color: C.text }}
                    >
                      {k}
                    </span>
                    <span
                      className="text-[10.5px] uppercase tracking-[0.22em] font-medium mt-2"
                      style={{ color: C.brand }}
                    >
                      {v}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking / Insight / Thought leadership */}
      <section
        data-testid="thought-leadership"
        style={{ background: C.surface }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <p
                className="text-[11px] uppercase tracking-[0.28em] font-medium mb-5"
                style={{ color: C.rose }}
              >
                Speaking · Insights · Thought leadership
              </p>
              <h2
                className="font-playfair font-normal text-3xl sm:text-4xl lg:text-[48px] leading-[1.06] tracking-tight"
                style={{ color: C.text }}
              >
                Where the conversation{" "}
                <em className="italic" style={{ color: C.brand }}>
                  already lives
                </em>
                .
              </h2>
            </div>
            <a
              href="#"
              className="group inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold pb-1.5 border-b transition-all"
              style={{ color: C.brand, borderColor: C.brand }}
            >
              All speaking &amp; insights
              <ArrowUpRight
                size={14}
                weight="bold"
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {thoughtLeadership.map((t, i) => (
              <article
                key={i}
                data-testid={`thought-${i}`}
                className="group rounded-2xl p-7 lg:p-8 border transition-all duration-300 flex flex-col"
                style={{ background: C.porcelain, borderColor: `${C.text}1F` }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = `${C.brand}66`;
                  e.currentTarget.style.transform = "translateY(-4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${C.text}1F`;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{ background: C.brand }}
                    />
                    <span
                      className="text-[10.5px] uppercase tracking-[0.24em] font-semibold"
                      style={{ color: C.brand }}
                    >
                      {t.type}
                    </span>
                  </span>
                  <span
                    className="text-[10.5px] uppercase tracking-[0.22em] font-medium"
                    style={{ color: `${C.text}A0` }}
                  >
                    {t.label}
                  </span>
                </div>
                <h3
                  className="font-playfair font-normal text-[22px] lg:text-[23px] leading-[1.22] tracking-tight flex-1 mb-8"
                  style={{ color: C.text }}
                >
                  {t.title}
                </h3>
                <div
                  className="pt-5 border-t flex items-center justify-between"
                  style={{ borderColor: `${C.text}1F` }}
                >
                  <span
                    className="text-[11.5px] uppercase tracking-[0.2em] font-medium"
                    style={{ color: `${C.text}A8` }}
                  >
                    {t.meta}
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.2em] font-semibold group-hover:gap-2.5 transition-all"
                    style={{ color: C.brand }}
                  >
                    Read
                    <ArrowRight size={13} weight="bold" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry CTA — warm but institutionally anchored */}
      <section id="inquiry" data-testid="inquiry-cta" style={{ background: C.bg }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div
            className="relative rounded-[32px] p-10 lg:p-16 border overflow-hidden"
            style={{ background: `${C.surface}99`, borderColor: `${C.text}1F` }}
          >
            <div
              aria-hidden="true"
              className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full blur-3xl"
              style={{ background: `${C.rose}26` }}
            />
            <div className="relative grid lg:grid-cols-12 gap-12 items-end">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3 mb-7">
                  <span
                    className="inline-block h-px w-10"
                    style={{ background: C.brand }}
                  />
                  <p
                    className="text-[11px] uppercase tracking-[0.28em] font-medium"
                    style={{ color: C.brand }}
                  >
                    For executive directors, boards &amp; HR leaders
                  </p>
                </div>
                <h2
                  className="font-playfair font-normal text-4xl sm:text-5xl lg:text-[72px] leading-[1.04] tracking-tight"
                  style={{ color: C.text }}
                >
                  Tell us what your organization is{" "}
                  <em className="italic" style={{ color: C.brand }}>
                    carrying
                  </em>
                  .
                </h2>
                <p
                  className="mt-9 max-w-2xl text-[17.5px] lg:text-[18.5px] leading-[1.6]"
                  style={{ color: `${C.text}D5` }}
                >
                  A short, honest note is enough — context on the moment, who
                  is affected, and what kind of support you&rsquo;re looking
                  for. We respond within two business days, either with a time
                  to talk or a thoughtful referral if Faith&nbsp;1st isn&rsquo;t
                  the right fit.
                </p>
              </div>
              <div className="lg:col-span-4 lg:justify-self-end space-y-4 w-full lg:w-auto">
                <a
                  href="#"
                  data-testid="inquiry-primary-cta"
                  className="group inline-flex items-center justify-between w-full lg:w-[300px] gap-6 px-7 py-5 text-[13.5px] uppercase tracking-[0.16em] font-semibold rounded-full transition-colors"
                  style={{ background: C.brand, color: C.porcelain }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.inverse)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.brand)}
                >
                  Start the conversation
                  <ArrowRight
                    size={15}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
                <a
                  href="#"
                  data-testid="inquiry-secondary-cta"
                  className="group inline-flex items-center justify-between w-full lg:w-[300px] gap-6 border bg-transparent px-7 py-5 text-[13.5px] uppercase tracking-[0.16em] font-medium rounded-full transition-colors"
                  style={{ borderColor: `${C.text}55`, color: C.text }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.brand)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${C.text}55`)}
                >
                  Send a referral
                  <ArrowUpRight
                    size={15}
                    weight="bold"
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: C.inverse, color: C.porcelain }}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <BrandMark variant="dark" size="lg" />
              <p
                className="font-playfair italic text-[17px] mt-5 max-w-xs"
                style={{ color: C.rose }}
              >
                {TAGLINE}
              </p>
              <p
                className="text-[14.5px] leading-[1.7] max-w-xs mt-5"
                style={{ color: `${C.porcelain}D0` }}
              >
                Advisory practice for mission-driven leaders carrying real
                organizational weight.
              </p>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-8">
              {[
                ["Practice", ["Advisory", "Supervisory Readiness", "Coaching", "Facilitation", "Speaking"]],
                ["About", ["Approach", "Katrina", "Insights"]],
                ["Connect", ["Consultation", "LinkedIn", "Referral"]],
              ].map(([title, items]) => (
                <div key={title} className="space-y-3">
                  <div
                    className="text-[10.5px] uppercase tracking-[0.26em] mb-5 pb-3 border-b font-semibold"
                    style={{ color: C.rose, borderColor: `${C.porcelain}26` }}
                  >
                    {title}
                  </div>
                  {items.map((i) => (
                    <a
                      key={i}
                      href="#"
                      className="block text-[14px] transition-colors"
                      style={{ color: `${C.porcelain}E0` }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = C.rose)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = `${C.porcelain}E0`)}
                    >
                      {i}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div
            className="mt-14 pt-6 border-t flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-[0.24em] font-medium"
            style={{ borderColor: `${C.porcelain}26`, color: `${C.porcelain}B0` }}
          >
            <span>© {new Date().getFullYear()} Faith 1st · Concept 03</span>
            <span style={{ color: C.rose }}>Human-Centered Authority</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
